import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import PageTitle from '../hooks/PageTitle';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    practiceArea: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare data for API (combine first/last name)
      const contactData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        message: formData.practiceArea 
          ? `Practice Area: ${formData.practiceArea}\n\n${formData.message}` 
          : formData.message
      };

      // Send to backend API
      await axios.post(`${API_URL}/api/contact/`, contactData);
      
      toast({
        title: "Message Sent Successfully",
        description: "Thank you for contacting Ashbourne Law. A member of our team will respond within 24 hours.",
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        practiceArea: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        title: "Error Sending Message",
        description: "There was a problem submitting your message. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Contact Us" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-serif mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              We welcome the opportunity to discuss your legal requirements and explore how we can assist
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-3xl font-serif text-gray-900 mb-6">Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Reach out to our team for a confidential consultation about your legal needs.
                </p>
              </div>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900 mb-1">London Office</h3>
                      <p className="text-sm text-gray-600">
                        45 Gresham Street<br />
                        London EC2V 7BG
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900 mb-1">Phone</h3>
                      <a href="tel:+442071234567" className="text-sm text-gray-600 hover:text-gray-900">
                        +44 20 7123 4567
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-medium text-gray-900 mb-1">Email</h3>
                      <a href="mailto:contact@ashbournelaw.com" className="text-sm text-gray-600 hover:text-gray-900">
                        contact@ashbournelaw.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-medium text-gray-900 mb-2">Response Time</h3>
                <p className="text-sm text-gray-600">
                  We aim to respond to all inquiries within 24 hours during business days. For urgent matters, please call our main office line.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8 md:p-10">
                  <h2 className="text-3xl font-serif text-gray-900 mb-2">Send Us a Message</h2>
                  <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you promptly</p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="company">Company / Organization</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="practiceArea">Practice Area of Interest</Label>
                      <Select 
                        value={formData.practiceArea}
                        onValueChange={(value) => setFormData({...formData, practiceArea: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a practice area" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corporate">Corporate Law</SelectItem>
                          <SelectItem value="commercial">Commercial Law</SelectItem>
                          <SelectItem value="ma">Cross-border M&A</SelectItem>
                          <SelectItem value="contract">Contract Law</SelectItem>
                          <SelectItem value="governance">Corporate Governance</SelectItem>
                          <SelectItem value="arbitration">Arbitration</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="mt-1"
                        placeholder="Please provide details about your legal requirements..."
                      />
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-600">
                        By submitting this form, you acknowledge that any information provided is not confidential until a formal attorney-client relationship has been established. For confidential matters, please contact us directly by phone.
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-gray-900 text-white hover:bg-gray-800"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Contact Methods */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Other Offices</h2>
            <p className="text-lg text-gray-600">We have offices in multiple locations to serve you better</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6 text-center">
                <h3 className="font-serif text-xl text-gray-900 mb-2">Luxembourg</h3>
                <p className="text-sm text-gray-600 mb-3">European Hub (In Development)</p>
                <a href="tel:+442071234567" className="text-sm text-gray-700 hover:text-gray-900">+44 20 7123 4567</a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6 text-center">
                <h3 className="font-serif text-xl text-gray-900 mb-2">Geneva, Switzerland</h3>
                <p className="text-sm text-gray-600 mb-3">Swiss Office (In Development)</p>
                <a href="tel:+442071234567" className="text-sm text-gray-700 hover:text-gray-900">+44 20 7123 4567</a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6 text-center">
                <h3 className="font-serif text-xl text-gray-900 mb-2">Dubai</h3>
                <p className="text-sm text-gray-600 mb-3">DIFC, Gate Village 10</p>
                <a href="tel:+97141234567" className="text-sm text-gray-700 hover:text-gray-900">+971 4 123 4567</a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
